/**
 * PATH: apps/quote/src/components/builder/sheets/terms-sheet/template-merge-helpers.ts
 * FILE_ID: QB-TERMS-TEMPLATE-MERGE-001
 * PURPOSE: Pure I→O helpers for the SAM-style "Add missing lines"
 *          action on the Terms / Quote-Notes editor. Compares the
 *          rendered template's prose against the user's current notes
 *          line-by-line and returns the template lines that don't
 *          appear in the current text.
 * WHY: SAM's quote-notes editor has a "Replace with template" button
 *      AND an "Add missing lines" button. Quotech only had Replace.
 *      "Add missing lines" lets the estimator iteratively upgrade a
 *      hand-written notes block without wiping their edits.
 * FLOW:
 *   ┌─────────────┐    ┌──────────────────┐    ┌──────────────────────┐
 *   │ HTML in     │ →  │ extract lines    │ →  │ filter to lines not  │
 *   │ (template + │    │ (strip tags, trim│    │ found in current     │
 *   │  current)   │    │  blanks)         │    │ → array of strings   │
 *   └─────────────┘    └──────────────────┘    └──────────────────────┘
 * RELATED:
 *   - TermsSheet.tsx — consumer
 *   - useQuoteNotesActions.ts — bound to the "Add missing lines" button
 */

/**
 * Extract a list of normalised text lines from an HTML fragment.
 * Lines are derived from `<p>`, `<li>`, and `<br>` boundaries so the
 * comparison feels natural to how estimators write their notes.
 * Each line is trimmed and lower-cased for the comparison only — the
 * returned array preserves the ORIGINAL text so the appender can use
 * it verbatim.
 */
export function extractLines(html: string): string[] {
  if (!html) return []
  // Split on common block / line boundaries. We don't need a real
  // HTML parser — the goal is approximate matching, not perfect
  // round-tripping.
  const blockBreaks = html
    .replace(/<\/p>/gi, '\n')
    .replace(/<\/li>/gi, '\n')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/h[1-6]>/gi, '\n')
  // Strip remaining tags.
  const text = blockBreaks.replace(/<[^>]+>/g, '')
  // Decode the two most common HTML entities to avoid spurious
  // mismatches like "&amp;" vs "&".
  const decoded = text.replace(/&amp;/g, '&').replace(/&nbsp;/g, ' ')
  return decoded
    .split(/\n+/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0)
}

/**
 * Normalise a line for membership comparison: lowercase, collapse
 * runs of whitespace, strip trailing punctuation.
 */
export function normaliseLine(line: string): string {
  return line
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .replace(/[.,;:!?]+$/, '')
    .trim()
}

/**
 * Returns the lines from ``templateHtml`` that DON'T appear in
 * ``currentHtml`` (after normalisation). Used by the SAM-style
 * "Add missing lines" button.
 */
export function computeMissingLines(
  currentHtml: string,
  templateHtml: string,
): string[] {
  const current = new Set(extractLines(currentHtml).map(normaliseLine))
  return extractLines(templateHtml).filter((l) => !current.has(normaliseLine(l)))
}

/**
 * Append the supplied lines to the current HTML notes as a fresh
 * paragraph block. We deliberately don't try to merge into existing
 * <ul>/<ol> structures — appending discrete paragraphs is predictable
 * and lets the estimator re-format manually if they want a list.
 */
export function appendLinesAsParagraphs(
  currentHtml: string,
  lines: string[],
): string {
  if (lines.length === 0) return currentHtml
  const block = lines
    .map((l) => `<p>${escapeHtml(l)}</p>`)
    .join('')
  // Drop trailing whitespace + a single trailing empty paragraph so
  // the appended block doesn't double-space.
  const trimmed = currentHtml.replace(/(\s|<p>\s*<\/p>)+$/i, '')
  return trimmed ? `${trimmed}${block}` : block
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}
